import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMode } from '../contexts/ModeContext';
import { AppShell } from '../components/AppShell';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Notebook, Buildings, MapPin, CaretRight, ArrowLeft,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function JournalHubPage() {
  const navigate = useNavigate();
  const { mode } = useMode();
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchChantiers = useCallback(async () => {
    try {
      const type = mode === 'csps' ? 'csps' : 'opc';
      const res = await axios.get(`${API}/chantiers?type=${type}`, { withCredentials: true });
      setChantiers((res.data || []).filter(c => c && c.id));
    } catch { setChantiers([]); }
    finally { setLoading(false); }
  }, [mode]);

  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);

  if (loading) return (
    <AppShell><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div></AppShell>
  );

  return (
    <AppShell>
      <div className="space-y-4 max-w-3xl mx-auto" data-testid="journal-hub-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
            onClick={() => navigate('/dashboard')} data-testid="journal-hub-back">
            <ArrowLeft size={22} />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#111827]" data-testid="journal-hub-title">
              Journal de chantier
            </h1>
            <p className="text-sm text-[#4B5563]">Selectionnez un chantier pour consulter son journal</p>
          </div>
        </div>

        <div className="grid gap-3">
          {chantiers.length > 0 ? chantiers.map(c => (
            <Card key={c.id} className="border hover:shadow-md transition-shadow cursor-pointer active:scale-[0.98]"
              onClick={() => navigate(`/chantiers/${c.id}/journal`)}
              data-testid={`journal-hub-item-${c.id}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Notebook size={20} className="text-[#FF6B00]" weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#111827] text-sm truncate">{c.name}</p>
                  <p className="text-xs text-[#4B5563] flex items-center gap-1 truncate">
                    <MapPin size={10} className="flex-shrink-0" /> {c.address || 'Adresse non renseignee'}
                  </p>
                </div>
                <CaretRight size={18} className="text-gray-400 flex-shrink-0" />
              </CardContent>
            </Card>
          )) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Buildings size={40} className="mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-500">Aucun chantier</p>
                <p className="text-sm text-gray-400 mt-1">Creez un chantier pour commencer votre journal</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppShell>
  );
}
